import React, { useEffect } from "react";
import { useAuth } from "../Contexts/AuthContext";

export default function SecurityWrapper({ children }) {
  const { user } = useAuth();
  const preventSSCopy = user?.organization_details?.prevent_ss_copy ?? false;

  useEffect(() => {
    if (!preventSSCopy) return;

    // 1. Block Copy Action & User Selection
    const handleCopy = (e) => {
      e.preventDefault();
      toastAlert();
    };

    // Helper to alert user
    let lastAlertTime = 0;
    const toastAlert = () => {
      const now = Date.now();
      if (now - lastAlertTime > 2000) {
        // Dynamic import to avoid circular references/load issues
        import("react-hot-toast").then(({ default: toast }) => {
          toast.error("Security Policy: Copying and screenshots are restricted on this workspace.", {
            id: "security-alert"
          });
        });
        lastAlertTime = now;
      }
    };

    // 2. Block Keyboard Combos (Ctrl+C, Ctrl+P, PrintScreen)
    const handleKeyDown = (e) => {
      // PrintScreen check
      if (e.key === "PrintScreen" || e.keyCode === 44) {
        navigator.clipboard?.writeText?.("").catch(() => {});
        toastAlert();
      }

      // Ctrl + P or Cmd + P
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        toastAlert();
      }

      // Ctrl + C or Cmd + C
      if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        e.preventDefault();
        toastAlert();
      }
      
      // Ctrl + Shift + I or F12 (DevTools block - optional but standard for premium security)
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "I") ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "J")
      ) {
        e.preventDefault();
        toastAlert();
      }
    };

    // 3. Block Context Menu (Right Click)
    const handleContextMenu = (e) => {
      e.preventDefault();
      toastAlert();
    };

    // 4. Blur content when focus is lost (Prevents Snipping Tool / OS-level capture overlays)
    const handleBlur = () => {
      document.body.classList.add("security-blurred-content");
    };

    const handleFocus = () => {
      document.body.classList.remove("security-blurred-content");
    };

    // Apply classes and event listeners
    document.addEventListener("copy", handleCopy);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    
    // Add no-select class to body
    document.body.classList.add("security-no-select");

    // Clean up
    return () => {
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      document.body.classList.remove("security-blurred-content");
      document.body.classList.remove("security-no-select");
    };
  }, [preventSSCopy]);

  return (
    <>
      {/* Self-contained style injection to ensure security css classes are loaded */}
      {preventSSCopy && (
        <style dangerouslySetInnerHTML={{ __html: `
          .security-blurred-content {
            filter: blur(25px) !important;
            transition: filter 0.1s ease-in-out;
            pointer-events: none !important;
          }
          .security-no-select, .security-no-select * {
            user-select: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
          }
          @media print {
            body {
              display: none !important;
            }
          }
        `}} />
      )}
      {children}
    </>
  );
}
